import type { DataProvider } from 'react-admin';
import type { DebtSlip, PaymentOrder, Settings, Supplier, SupplierBankAccount, VatInvoice } from '../types';
import { nameSimilarity } from './matching';

const digits = (value?: string) => (value || '').replace(/\D/g, '');
const runningCreations = new Map<string, Promise<PaymentOrder>>();

async function withBrowserLock<T>(key: string, task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? (navigator as any).locks : undefined;
  return locks?.request ? locks.request(`payment-order:${key}`, task) : task();
}

export function getPrimarySupplierBank(supplier: Supplier): SupplierBankAccount | null {
  const accounts = supplier.bankAccounts || [];
  const primary = accounts.find((account) => account.isPrimary) || (accounts.length === 1 ? accounts[0] : undefined);
  if (primary) {
    return {
      ...primary,
      accountHolder: primary.accountHolder || supplier.accountHolder || supplier.name,
    };
  }
  if (supplier.accountNumber) {
    return {
      accountNumber: supplier.accountNumber,
      bankName: supplier.bankName,
      bankBin: supplier.bankBin,
      accountHolder: supplier.accountHolder || supplier.name,
      isPrimary: true,
      mappingSource: 'manual',
    };
  }
  return null;
}

export function findSupplierForInvoice(suppliers: Supplier[], invoice: VatInvoice): Supplier | undefined {
  const taxCode = digits(invoice.taxCode);
  if (taxCode) {
    const exact = suppliers.filter((supplier) => digits(supplier.taxCode) === taxCode);
    if (exact.length === 1) return exact[0];
    if (exact.length > 1) return undefined;
  }
  const ranked = suppliers
    .map((supplier) => ({ supplier, score: nameSimilarity(supplier.name, invoice.supplierName) }))
    .sort((a, b) => b.score - a.score);
  const first = ranked[0];
  const second = ranked[1];
  if (!first || first.score < 0.7 || (second && first.score - second.score < 0.15)) return undefined;
  return first.supplier;
}

export function createPaymentOrderForMatch(
  dp: DataProvider,
  slip: DebtSlip,
  invoice: VatInvoice,
  supplier: Supplier,
  settings: Settings,
): Promise<PaymentOrder> {
  const idempotencyKey = `debt-slip:${String(slip.id)}`;
  const current = runningCreations.get(idempotencyKey);
  if (current) return current;
  const task = withBrowserLock(idempotencyKey, async () => {
    const account = getPrimarySupplierBank(supplier);
    if (!account) throw new Error('Nhà cung cấp chưa có tài khoản ngân hàng chính.');
    if (!account.bankBin || !account.accountNumber || !account.accountHolder) {
      throw new Error('Tài khoản nhà cung cấp thiếu BIN, số tài khoản hoặc chủ tài khoản.');
    }
    const { data: existing } = await dp.getList<PaymentOrder>('paymentOrders', {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: 'createdAt', order: 'DESC' },
      filter: {},
    });
    const duplicate = existing.find((order) =>
      order.idempotencyKey === idempotencyKey || String(order.debtSlipId) === String(slip.id),
    );
    if (duplicate) return duplicate;

    const addInfo = `TT ${invoice.invoiceNumber}${settings.payerCompanyName ? ` ${settings.payerCompanyName}` : ''}`.trim();
    const { data } = await dp.create<PaymentOrder>('paymentOrders', {
      data: {
        idempotencyKey,
        debtSlipId: slip.id,
        vatInvoiceId: invoice.id,
        supplierId: supplier.id,
        supplierName: supplier.name || invoice.supplierName,
        amount: invoice.amount,
        bankName: account.bankName,
        bankBin: account.bankBin,
        accountNumber: account.accountNumber,
        accountHolder: account.accountHolder,
        addInfo,
        vietQrUrl: undefined,
        status: 'pending',
        createdAt: new Date().toISOString(),
      } as Omit<PaymentOrder, 'id'>,
    });
    return data;
  }).finally(() => runningCreations.delete(idempotencyKey));
  runningCreations.set(idempotencyKey, task);
  return task;
}
